<template>
  <LISATreeSelect
    v-if="isLISA3"
    v-bind="{
      ...$attrs,
      ...$props,
    }"
    v-model="modelValue"
    :items="nodes"
    :label="entityName"
    :dialog-title="
      $attrs.label
        ? String($attrs.label)
        : t('components.shared.searchSelect.TreeSelect.header', { entity: entityName })
    "
  >
    <template v-if="$slots.append" #append>
      <slot name="append"></slot>
    </template>
    <template v-if="$slots.prepend" #prepend>
      <slot name="prepend" />
    </template>
  </LISATreeSelect>
  <div
    v-else
    class="h-100"
    :class="{
      'p-inputwrapper-filled':
        modelValue != null && (!Array.isArray(modelValue) || modelValue.length > 0),
    }"
  >
    <div v-if="inline" class="inline-tree-select">
      <TreeSelectPanel
        v-model:collapsed="collapsedKeys"
        v-model:search-text="searchText"
        v-model:selected="selected"
        v-model:show-only-selected="showOnlySelected"
        :nodes="nodes"
        :selected-nodes="selectedNodes"
        :multiple="multiple"
        :hide-checkbox="hideCheckbox"
        :hide-path="hidePath"
        :disabled="disabled"
        @expand-all="expandAll"
        @collapse-all="collapseAll"
      />
    </div>
    <div v-else-if="showAsButton">
      <slot name="button" :disabled="disabled" :open-modal="openModal" @click="openModal">
        <LISAButton
          :label="t(`global.add`)"
          :disabled="disabled"
          icon="fas fa-plus"
          @click="openModal"
        />
      </slot>
    </div>
    <div
      v-else
      class="input-field"
      :class="{
        'cursor-pointer': !disabled,
        'p-valid': valid === true,

        'p-invalid': valid === false,
        'p-disabled': disabled,
        'badge-input':
          multiple && modelValue != null && Array.isArray(modelValue) && modelValue.length > 0,
      }"
      :style="{
        'background-color': disabled ? 'var(--p-disabled-color) !important' : undefined,
        'border-color': disabled ? 'var(--p-gray-400) !important' : undefined,
        color: disabled ? 'var(--p-surface-500) !important' : undefined,
      }"
      @click="openModal"
    >
      <!--i have to do the 18.4px for a fix for the height in modal-->
      <div
        style="flex-grow: 9; overflow: hidden; white-space: nowrap; text-overflow: ellipsis"
        :style="{
          height:
            multiple && modelValue != null && Array.isArray(modelValue) && modelValue.length > 0
              ? 'auto'
              : '18.4px',
        }"
      >
        <template v-if="multiple && (Array.isArray(modelValue) || modelValue == null)">
          <Chip
            v-for="(key, index) of modelValue?.slice(0, 5)"
            :key="`selected${key}`"
            style="white-space: nowrap; margin-right: 0.25rem"
            class="py-1"
            color="var(--p-surface-100)"
            removable
            @remove="removeSelectedEntry($event, index)"
          >
            {{ elementsMap.get(key.toString())?.[labelPropName] ?? t("global.deleted") }}
            [{{ t("global.id") }}: {{ key }}]
          </Chip>
          <Chip v-if="modelValue != null && modelValue.length > 5" class="py-1"> ... </Chip>
          <span
            v-if="modelValue == null || modelValue?.length === 0"
            style="background-color: transparent"
            class="tree-select-empty-value py-1"
            aria-hidden="true"
          >
            &nbsp;
          </span>
        </template>
        <template v-else-if="modelValue != null">
          <template v-if="!hidePath && elementsMap.has(modelValue.toString())">
            {{ elementsMap.get(modelValue.toString())?.[treePropName] }} |
          </template>

          {{ elementsMap.get(modelValue.toString())?.[labelPropName] ?? t("global.deleted") }}
          <span>[{{ t("global.id") }}: {{ modelValue }}]</span>
        </template>

        <template v-else>
          <span :style="{ color: placeholderOptions?.variant }">
            {{ placeholderOptions?.label }}
          </span>
        </template>
      </div>

      <div class="flex align-items-center">
        <i class="pi pi-chevron-down" style="margin-right: 0.25rem; color: var(--p-surface-400)" />
      </div>
    </div>
    <LModal
      v-if="!inline"
      v-model:visible="modalVisible"
      size="xl"
      :no-propagation="true"
      :title="t('components.shared.searchSelect.TreeSelect.header', { entity: entityName })"
      :ok-disabled="!clearable && Object.keys(selected).length === 0"
      test-name="tree-select"
      data-testid="tree-select-modal"
      :submit="
        async () => {
          await save();
          return { success: true };
        }
      "
      @show="reset"
    >
      <InputRow
        :model-value="selected"
        :display-name="t('components.shared.searchSelect.TreeSelect.onlySelected')"
        name="selected"
        type="hidden"
      />
      <TreeSelectPanel
        ref="treeSelectPanelRef"
        v-model:collapsed="collapsedKeys"
        v-model:search-text="searchText"
        v-model:selected="selected"
        v-model:show-only-selected="showOnlySelected"
        :nodes="nodes"
        :selected-nodes="selectedNodes"
        :multiple="multiple"
        :hide-checkbox="hideCheckbox"
        :hide-path="hidePath"
        @expand-all="expandAll"
        @collapse-all="collapseAll"
      >
        <template #itemControls="{ node }">
          <slot name="rowControls" :node="node" :select-only="selectOnly" />
        </template>
      </TreeSelectPanel>

      <template #footerExtra="{ okDisabled }">
        <div class="flex justify-content-between gap-1 align-items-center">
          <Message
            v-if="okDisabled"
            severity="error"
            style="margin-bottom: 1px"
            icon="fas fa-triangle-exclamation"
          >
            {{ t("components.shared.searchSelect.TreeSelect.selectEntryError") }}
          </Message>

          <LISAButton
            v-if="showInlineCreateComputed"
            :outlined="true"
            :label="
              t('components.shared.searchSelect.TreeSelect.createNew', {
                entity: entityName,
              })
            "
            icon="fas fa-plus"
            @click="$emit('showCreateModal')"
          />
        </div>
      </template>
    </LModal>
  </div>
</template>

<script setup lang="ts">
import LModal from "@/components/LModal.vue";
import Fuse from "fuse.js";
import { computed, nextTick, ref, watch, type PropType } from "vue";
import LISAButton from "../../primevue/LISAButton.vue";
import {
  compareTreeElementsByPath,
  type SortableTreeElement,
  type TreeNodeItem,
} from "./TreeSelectHelper";
import Message from "primevue/message";
import { useStore } from "vuex";
import { useTranslation } from "@/language/translation";
import InputRow from "../../form/InputRow.vue";
import Chip from "primevue/chip";
import TreeSelectPanel from "./TreeSelectPanel.vue";
import { LISATreeSelect } from "@fi-solutions/lisa-ui";
import { isLISA3 } from "@/composables/useLISA3.ts";

const props = defineProps({
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  placeholderOptions: {
    type: Object as PropType<{ label: string; variant: string }>,
    required: false,
    default: null,
  },
  entityName: {
    type: String,
    required: true,
  },
  showAsButton: {
    type: Boolean,
    required: false,
    default: false,
  },
  inline: {
    type: Boolean,
    required: false,
    default: false,
  },
  multiple: {
    type: Boolean,
    required: false,
    default: false,
  },
  elements: {
    type: Array as () => Record<string, any>[],
    required: true,
  },
  treePropName: {
    type: String,
    required: false,
    default: "path",
  },
  keyPropName: {
    type: String,
    required: false,
    default: "id",
  },
  labelPropName: {
    type: String,
    required: false,
    default: "name",
  },
  showInlineCreate: {
    type: Boolean,
    required: false,
    default: false,
  },
  loading: {
    type: Boolean,
    required: false,
    default: false,
  },
  autoSelectFirst: {
    type: Boolean,
    required: false,
    default: false,
  },
  preferredScrollId: {
    type: Number,
    required: false,
    default: undefined,
  },
  valid: {
    type: Boolean,
    required: false,
    default: undefined,
  },
  clearable: {
    type: Boolean,
    required: false,
    default: true,
  },
  disabledNodes: {
    type: Array as PropType<
      {
        key: string | number;
        includeChildren: boolean;
      }[]
    >,
    required: false,
    default: () => [],
  },
  createAccessRight: {
    type: String,
    required: false,
    default: undefined,
  },
  hideCheckbox: {
    type: Boolean,
    required: false,
    default: false,
  },
  hidePath: {
    type: Boolean,
    required: false,
    default: false,
  },
  sortFunction: {
    type: Function as PropType<(a: SortableTreeElement, b: SortableTreeElement) => number>,
    required: false,
    default: undefined,
  },
});

const modalVisible = defineModel("modalVisible", {
  type: Boolean,
  required: false,
  default: false,
});

const showOnlySelected = ref(false);

async function reset() {
  showOnlySelected.value = false;
  await nextTick();
  scrollToFirstSelected();
}

defineEmits<{
  showCreateModal: [];
}>();

const modelValue = defineModel("modelValue", {
  type: [Array, Number] as PropType<number | number[] | undefined>,
  required: false,
  default: undefined,
});

function openModal() {
  if (!props.disabled) {
    modalVisible.value = true;
    selected.value = (
      Array.isArray(modelValue.value)
        ? modelValue.value
        : modelValue.value != null
          ? [modelValue.value]
          : []
    ).reduce(
      (acc, val) => {
        acc[String(val)] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }
}

function save() {
  if (props.multiple) {
    modelValue.value = Object.keys(selected.value).map((key) => Number(key));
  } else {
    const val = Object.keys(selected.value)[0];
    modelValue.value = val != null ? Number(val) : undefined;
  }
}

function selectOnly(key: number | string) {
  selected.value = { [String(key)]: true };
}

function removeSelectedEntry(event: Event, index: number) {
  event.stopPropagation();
  if (Array.isArray(modelValue.value)) {
    modelValue.value.splice(index, 1);
  }
}

const elementsMap = computed(
  () => new Map(props.elements.map((el) => [String(el[props.keyPropName]), el])),
);

const collapsedKeys = ref<Record<string, boolean>>({});
const selected = ref<Record<string, boolean>>({});
const treeSelectPanelRef = ref<InstanceType<typeof TreeSelectPanel> | null>(null);

function syncInlineSelectionFromModel() {
  if (!props.inline) return;
  const ids = Array.isArray(modelValue.value)
    ? modelValue.value
    : modelValue.value == null
      ? []
      : [modelValue.value];
  const nextSelected = Object.fromEntries(ids.map((id) => [String(id), true]));
  const currentKeys = Object.keys(selected.value).sort();
  const nextKeys = Object.keys(nextSelected).sort();
  if (currentKeys.join("|") !== nextKeys.join("|")) selected.value = nextSelected;
}

watch([() => props.inline, modelValue], syncInlineSelectionFromModel, {
  deep: true,
  immediate: true,
});

watch(
  selected,
  (selection) => {
    if (!props.inline) return;
    const ids = Object.keys(selection).map(Number);
    if (props.multiple) {
      const currentIds = Array.isArray(modelValue.value) ? modelValue.value : [];
      if ([...currentIds].sort().join("|") !== [...ids].sort().join("|")) modelValue.value = ids;
      return;
    }
    const nextValue = ids[0];
    if (modelValue.value !== nextValue) modelValue.value = nextValue;
  },
  { deep: true },
);

const existingElementsTreeMap = computed(
  () =>
    new Map(
      props.elements.map((node) => [
        String(node[props.treePropName]),
        String(node[props.keyPropName]),
      ]),
    ),
);

const existingElementsKeyMap = computed(
  () =>
    new Map(
      props.elements.map((node) => [
        String(node[props.keyPropName]),
        String(node[props.treePropName]),
      ]),
    ),
);

const { t } = useTranslation();

const sortedElements = computed(() => {
  const existingPaths = new Set(props.elements.map((node) => String(node[props.treePropName])));
  const childrenOfElements = new Map<string, string[]>();

  props.elements.forEach((node) => {
    const parts = String(node[props.treePropName]).split(".");

    parts.slice(0, -1).forEach((_, index) => {
      const path = parts.slice(0, index + 1).join(".");
      if (!childrenOfElements.has(path)) {
        childrenOfElements.set(path, []);
      }
      childrenOfElements.get(path)?.push(String(node[props.treePropName]));
    });
  });

  childrenOfElements.forEach((children, parent) => {
    childrenOfElements.set(
      parent,
      children.map((childPath) => existingElementsTreeMap.value.get(childPath) as string),
    );
  });

  function getLabelPath(path: string) {
    if (props.sortFunction == null) {
      // only needed for custom sorting, otherwise the labelPath is not used and computing it is an unnecessary overhead
      return "";
    }
    const parts = path.split(".");

    return parts
      .map((_, index) => {
        const partialPath = parts.slice(0, index + 1).join(".");
        return (
          elementsMap.value.get(existingElementsTreeMap.value.get(partialPath) ?? "")?.name ??
          t("global.deleted")
        );
      })
      .join(" / ");
  }
  const mappedElements = props.elements.map((node) => ({
    id: node["id"],
    key: String(node[props.keyPropName]),
    label: String(node[props.labelPropName]),
    path: String(node[props.treePropName]),
    labelPath: getLabelPath(String(node[props.treePropName])),
    raw: node.raw,
    selectable: true,
    beforeIcon: node.beforeIcon,
    disabled:
      props.disabled ||
      props.disabledNodes.some(
        (disabledNode) =>
          disabledNode.key === String(node[props.keyPropName]) ||
          String(node[props.treePropName]) ===
            existingElementsKeyMap.value.get(disabledNode.key.toString())! ||
          (disabledNode.includeChildren &&
            String(node[props.treePropName]).startsWith(
              existingElementsKeyMap.value.get(disabledNode.key.toString())! + ".",
            )),
      ),
    hidden: false,
    children: childrenOfElements.get(String(node[props.treePropName])),
  }));

  const missingElements = new Set(
    mappedElements
      .flatMap((el) => {
        const parts = el.path.split(".");

        return parts.map((_, index) => parts.slice(0, index + 1).join("."));
      })
      .filter((path) => !existingPaths.has(path)),
  )
    .values()
    .map((path) => ({
      id: -1,
      key: "-1",
      path,
      labelPath: getLabelPath(path),
      label: t("global.deleted"),
      raw: null,
      selectable: false,
      disabled: true,
      hidden: false,
      beforeIcon: "",
      children: childrenOfElements.get(path),
    }));

  return [...mappedElements, ...missingElements].sort(
    props.sortFunction ?? compareTreeElementsByPath,
  );
});

const searchText = ref("");
const nodes = computed(() => {
  let elements = sortedElements.value;
  let searchResult: Set<string> | undefined;
  if (searchText.value) {
    const fuse = new Fuse(
      props.elements.map((el) => ({ ...el })),
      {
        isCaseSensitive: false,
        shouldSort: true,
        threshold: 0.3,
        ignoreLocation: true,
        minMatchCharLength: 1,
        keys: [props.treePropName, props.labelPropName, props.keyPropName],
      },
    );
    const searchResults = fuse
      .search(searchText.value)
      .map((el) => el.item[props.treePropName] as string);
    elements = sortedElements.value.filter((el) =>
      searchResults?.some((res) => res.startsWith(el.path)),
    );
    searchResult = new Set(searchResults);
  }

  return elements
    .filter((el) => {
      const parts = el.path.split(".");

      return !parts
        .slice(0, -1)
        .map((_, index) => parts.slice(0, index + 1).join("."))
        .some((path) => collapsedKeys.value[existingElementsTreeMap.value.get(path)!]);
    })
    .map((node) => ({
      ...node,
      hidden: searchResult != null && !searchResult.has(node.path),
    }));
});

const selectedNodes = computed<TreeNodeItem[]>(() =>
  Object.keys(selected.value).map((key) => ({
    key,
    label: String(elementsMap.value.get(key)?.[props.labelPropName] ?? ""),
    path: String(elementsMap.value.get(key)?.[props.treePropName] ?? ""),
    beforeIcon: elementsMap.value.get(key)?.beforeIcon,
  })),
);

function scrollToFirstSelected() {
  const selectedKeys = Object.keys(selected.value);
  const scrollTargetId =
    selectedKeys.length > 0 ? selectedKeys[0] : props.preferredScrollId?.toString();
  treeSelectPanelRef.value?.scrollToKey(scrollTargetId);
}

function collapseNode(node: TreeNodeItem) {
  if (node.children) {
    collapsedKeys.value[node.key] = true;

    for (let child of node.children) {
      collapsedKeys.value[child] = true;
    }
  }
}

function expandAll() {
  collapsedKeys.value = {};
}

function collapseAll() {
  for (let node of nodes.value) {
    collapseNode(node);
  }
}

function collapseDisabledNodes() {
  props.disabledNodes.forEach((disabledNode) => {
    if (disabledNode.includeChildren) {
      collapsedKeys.value[disabledNode.key] = true;
    }
  });
}

watch(
  () => props.elements,
  () => {
    if (
      props.autoSelectFirst &&
      (modelValue.value == null ||
        (props.multiple && Array.isArray(modelValue.value) && modelValue.value.length === 0))
    ) {
      const firstCostCenter = nodes.value?.[0];
      if (firstCostCenter?.key != null) {
        if (props.multiple) {
          modelValue.value = [Number(firstCostCenter.key)];
        } else {
          modelValue.value = Number(firstCostCenter.key);
        }
      }
    }
    expandAll();
    collapseDisabledNodes();
  },
  { immediate: true },
);

const store = useStore();

const showInlineCreateComputed = computed(() => {
  return (
    props.showInlineCreate &&
    (props.createAccessRight == null ||
      store.getters["login/hasAccessRight"](props.createAccessRight))
  );
});

watch(
  () => props.disabledNodes,
  () => {
    collapseDisabledNodes();
  },
  {
    immediate: true,
  },
);
</script>

<style lang="scss" scoped>
.input-field {
  border: 1px solid var(--p-surface-300);
  color: var(--p-surface-700);
  border-radius: var(--p-border-radius-md);
  outline-color: transparent;
  background-color: var(--p-surface-0);
  padding-block: var(--p-form-field-padding-y);
  padding-inline: var(--p-form-field-padding-x);
  display: flex;
  gap: 0.5rem;
  flex-wrap: no-wrap;
}
.badge-input {
  padding-block: calc(var(--p-form-field-padding-y) / 2) !important;
  padding-inline: calc(var(--p-form-field-padding-x) / 2) !important;
}

.inline-tree-select {
  display: flex;
  width: 100%;
  min-height: 18rem;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--p-form-field-border-color);
  border-radius: var(--p-border-radius-md);
  background: var(--p-form-field-background);
}
</style>
